"use client";

import { Continent } from "@/types/continent";
import { useRouter } from "next/navigation";
import * as React from "react";
import HeaderMapSvg from "./HeaderMapSvg";

type Props = {
  selected?: Continent | null;
  onSelect?: (continent: Continent) => void;
  className?: string;
  continentCounts?: Record<Continent, number>;
};

export default function HeaderMap({
  selected,
  onSelect,
  className,
  continentCounts,
}: Props) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const planeLayerRef = React.useRef<HTMLDivElement | null>(null);

  const isMountedRef = React.useRef(false);

  React.useEffect(() => {
    isMountedRef.current = true;
  }, []);

  const router = useRouter();

  const counts: Record<Continent, number> = continentCounts ?? {
    africa: 0,
    asia: 0,
    europe: 0,
    "north-america": 0,
    "latin-america": 0,
    oceania: 0,
    "south-america": 0,
  };

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const target = e.target as Element | null;

    let g: SVGGElement | null = null;
    let el: Element | null = target;

    while (el && el !== svgRef.current) {
      if (el instanceof SVGGElement && el.id) {
        g = el;
        break;
      }
      el = el.parentElement;
    }

    if (!g) return;

    const continent = g.id as Continent;
    const count = counts[continent] ?? 0;

    // 🚫 hard block if no tours
    if (count === 0) return;

    router.prefetch("/vylety");

    flyTo(g);
    onSelect?.(continent);

    setTimeout(() => {
      router.push(`/vylety?continent=${continent}`);
    }, 1700);
  };

  const flyTo = (g: SVGGElement) => {
    if (!isMountedRef.current) return;

    const svg = svgRef.current!;
    const europe = svg.querySelector<SVGGElement>("#europe");
    if (!europe) return;

    const europeRect = europe.getBoundingClientRect();
    const targetRect = g.getBoundingClientRect();
    const overlayRect = planeLayerRef.current!.getBoundingClientRect();

    let startX = europeRect.left + europeRect.width / 2 - 130;
    let startY = europeRect.top + europeRect.height / 2 - overlayRect.top;

    let endX: number;
    let endY: number;

    if (g.id === "europe") {
      // Europe: reverse direction (short takeoff, then land back in Europe)
      const reverseStartX = startX + 120;
      const reverseStartY = startY - 60;

      // swap start/end
      endX = startX;
      endY = startY;

      // override start
      startX = reverseStartX;
      startY = reverseStartY;
    } else {
      endX = targetRect.left + targetRect.width / 2 + overlayRect.left * 1.5;
      endY = targetRect.top + targetRect.height / 2 - overlayRect.top * 1.5;
    }

    // ✅ CORRECT angle: local start → local end
    const angleDeg =
      (Math.atan2(endY - startY, endX - startX) * 180) / Math.PI + 50;

    if (!planeLayerRef.current) return;

    const plane = document.createElement("div");
    plane.textContent = "✈️";
    plane.style.position = "absolute";
    plane.style.left = `${startX}px`;
    plane.style.top = `${startY}px`;
    plane.style.fontSize = "80px";
    plane.style.transformOrigin = "center";
    plane.style.pointerEvents = "none";
    plane.style.opacity = "1";
    plane.style.visibility = "visible";
    plane.style.zIndex = "9999";

    planeLayerRef.current.appendChild(plane);
    plane.getBoundingClientRect(); // force initial paint before animation

    plane.animate(
      [
        {
          transform: `translate(0px, 0px) rotate(${angleDeg}deg)`,
        },
        {
          transform: `translate(${endX - startX}px, ${
            endY - startY
          }px) rotate(${angleDeg}deg)`,
        },
      ],
      {
        duration: 1600,
        easing: "ease-in-out",
        fill: "forwards",
      },
    );

    setTimeout(() => {
      plane.remove();
    }, 4000);
  };

  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    svg.querySelectorAll<SVGGElement>("g[id]").forEach((g) => {
      g.classList.toggle("active", g.id === selected);
    });
  }, [selected]);

  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    svg.querySelectorAll<SVGGElement>("g[id]").forEach((g) => {
      const id = g.id as Continent;
      const hasTours = counts[id] > 0;
      g.classList.toggle("disabled", !hasTours);
      g.style.pointerEvents = hasTours ? "auto" : "none";
      g.style.cursor = hasTours ? "pointer" : "default";
    });
  }, [counts]);
  return (
    <div ref={containerRef} className='relative w-full h-[50vh]'>
      <HeaderMapSvg
        ref={svgRef}
        onClick={handleClick}
        className='w-full h-[50vh] md:h-[60vh] xl:h-[70vh]'
        counts={counts}
      />
      <div
        ref={planeLayerRef}
        className='absolute inset-0 pointer-events-none z-50'
      />
    </div>
  );
}
